// src/pages/Analyze.js
// Multi-tab analytics dashboard — ADMIN only

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Row, Col, Card, CardBody, CardHeader, CardTitle, CardFooter,
    Nav, NavItem, NavLink, TabContent, TabPane,
    Table, Badge, Input, InputGroup, InputGroupAddon, InputGroupText,
    Button, ButtonGroup, Collapse, Spinner, Label,
    Pagination, PaginationItem, PaginationLink
} from 'reactstrap';
import { Line, Doughnut, Bar, HorizontalBar } from 'react-chartjs-2';
import analyticsService from 'services/analytics.service';

// ═══════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════
const COLORS = {
    primary: '#5e72e4',
    success: '#2dce89',
    danger: '#f5365c',
    warning: '#fb6340',
    info: '#11cdef',
    dark: '#344767',
    muted: '#8898aa',
    api: '#5e72e4',
    api2: '#2dce89',
    api3: '#fb6340',
};

const CHART_COLORS = ['#5e72e4', '#2dce89', '#fb6340', '#f5365c', '#11cdef', '#ffd600', '#8965e0', '#f3a4b5'];

const DATE_RANGES = [
    { label: 'Today', value: 'today' },
    { label: '7 days', value: '7days' },
    { label: '30 days', value: '30days' },
];

const TOOLS = [
    { label: 'All Tools', value: '' },
    { label: 'DHB Stream Pro', value: 'dhbstreampro' },
    { label: 'DHB Render', value: 'dhbrender' },
    { label: 'DHB Reup YouTube', value: 'dhbreupyoutube' },
    { label: 'DHB Reup TikTok', value: 'dhbreuptiktok' },
    { label: 'DHB Reup Facebook', value: 'dhbreupfacebook' },
    { label: 'DHB Reup Instagram', value: 'dhbreupinstagram' },
    { label: 'DHB Upload X', value: 'dhbreuptwitter' },
    { label: 'DHB Reup Pinterest', value: 'dhbreuppinterest' },
    { label: 'DHB YouTube Viewers', value: 'dhbyoutubeviewers' },
    { label: 'DHB Stream Facebook', value: 'dhbstreamfacebook' },
];

// ═══════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════
function getDateRange(range) {
    const to = new Date();
    let from = new Date();
    if (range === '7days') from.setDate(from.getDate() - 7);
    else if (range === '30days') from.setDate(from.getDate() - 30);
    else from.setHours(0, 0, 0, 0); // today
    return {
        from: from.toISOString().split('T')[0],
        to: to.toISOString().split('T')[0],
    };
}

function formatNumber(n) {
    if (!n && n !== 0) return '-';
    return n.toLocaleString();
}

function formatMs(ms) {
    if (!ms && ms !== 0) return '-';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
}

function timeAgo(dateStr) {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    if (diff < 60000) return `${Math.floor(diff / 1000)}s ago`;
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return new Date(dateStr).toLocaleDateString();
}

function statusBadge(code) {
    if (!code) return null;
    if (code >= 500) return <Badge color="danger" pill>{code}</Badge>;
    if (code >= 400) return <Badge color="warning" pill>{code}</Badge>;
    return <Badge color="success" pill>{code}</Badge>;
}

// ═══════════════════════════════════════════════
// PAGINATION COMPONENT
// ═══════════════════════════════════════════════
const PaginationBar = ({ page, totalPages, onPageChange }) => {
    if (!totalPages || totalPages <= 1) return null;
    const pages = [];
    const start = Math.max(1, page - 2);
    const end = Math.min(totalPages, page + 2);
    for (let i = start; i <= end; i++) pages.push(i);

    return (
        <Pagination size="sm" className="justify-content-center mt-3">
            <PaginationItem disabled={page <= 1}>
                <PaginationLink previous onClick={() => onPageChange(page - 1)} />
            </PaginationItem>
            {start > 1 && (
                <PaginationItem>
                    <PaginationLink onClick={() => onPageChange(1)}>1</PaginationLink>
                </PaginationItem>
            )}
            {start > 2 && <PaginationItem disabled><PaginationLink>...</PaginationLink></PaginationItem>}
            {pages.map(p => (
                <PaginationItem key={p} active={p === page}>
                    <PaginationLink onClick={() => onPageChange(p)}>{p}</PaginationLink>
                </PaginationItem>
            ))}
            {end < totalPages - 1 && <PaginationItem disabled><PaginationLink>...</PaginationLink></PaginationItem>}
            {end < totalPages && (
                <PaginationItem>
                    <PaginationLink onClick={() => onPageChange(totalPages)}>{totalPages}</PaginationLink>
                </PaginationItem>
            )}
            <PaginationItem disabled={page >= totalPages}>
                <PaginationLink next onClick={() => onPageChange(page + 1)} />
            </PaginationItem>
        </Pagination>
    );
};

// ═══════════════════════════════════════════════
// KPI CARD COMPONENT
// ═══════════════════════════════════════════════
const KpiCard = ({ icon, label, value, subValue, color = COLORS.primary }) => (
    <Card style={{
        borderRadius: '16px',
        border: 'none',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        overflow: 'hidden',
        marginBottom: '16px',
    }}>
        <CardBody style={{ padding: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{
                    width: '52px', height: '52px', borderRadius: '14px',
                    background: `linear-gradient(135deg, ${color}22, ${color}44)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                }}>
                    <i className={icon} style={{ fontSize: '20px', color }} />
                </div>
                <div>
                    <div style={{ fontSize: '12px', color: COLORS.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {label}
                    </div>
                    <div style={{ fontSize: '24px', fontWeight: 700, color: COLORS.dark, lineHeight: 1.2 }}>
                        {value}
                    </div>
                    {subValue && <div style={{ fontSize: '12px', color: COLORS.muted, marginTop: '2px' }}>{subValue}</div>}
                </div>
            </div>
        </CardBody>
    </Card>
);

// ═══════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════
const AnalyzePage = () => {
    // Global filters
    const [dateRange, setDateRange] = useState('today');
    const [toolFilter, setToolFilter] = useState('');
    const [activeTab, setActiveTab] = useState('overview');

    // Tab data states
    const [overview, setOverview] = useState(null);
    const [users, setUsers] = useState(null);
    const [endpoints, setEndpoints] = useState(null);
    const [errors, setErrors] = useState(null);
    const [live, setLive] = useState(null);

    // Loading states
    const [loading, setLoading] = useState({});

    // Tab-specific filters
    const [userSearch, setUserSearch] = useState('');
    const [userApp, setUserApp] = useState('');
    const [userPage, setUserPage] = useState(1);
    const [endpointSearch, setEndpointSearch] = useState('');
    const [endpointSort, setEndpointSort] = useState('calls');
    const [endpointPage, setEndpointPage] = useState(1);
    const [errorSearch, setErrorSearch] = useState('');
    const [errorStatus, setErrorStatus] = useState('');
    const [errorPage, setErrorPage] = useState(1);

    // Collapse states
    const [expandedEndpoints, setExpandedEndpoints] = useState({});
    const [expandedErrors, setExpandedErrors] = useState({});

    // Live monitor
    const [liveEnabled, setLiveEnabled] = useState(true);
    const liveTimerRef = useRef(null);

    // ─── Data fetchers ───
    const { from, to } = getDateRange(dateRange);

    const fetchOverview = useCallback(() => {
        setLoading(l => ({ ...l, overview: true }));
        analyticsService.getOverview(from, to, toolFilter)
            .then(res => setOverview(res.data?.data || res.data))
            .catch(() => setOverview(null))
            .finally(() => setLoading(l => ({ ...l, overview: false })));
    }, [from, to, toolFilter]);

    const fetchUsers = useCallback(() => {
        setLoading(l => ({ ...l, users: true }));
        analyticsService.getUserBehavior(from, to, {
            tool: toolFilter, search: userSearch, app: userApp, page: userPage
        })
            .then(res => setUsers(res.data?.data || res.data))
            .catch(() => setUsers(null))
            .finally(() => setLoading(l => ({ ...l, users: false })));
    }, [from, to, toolFilter, userSearch, userApp, userPage]);

    const fetchEndpoints = useCallback(() => {
        setLoading(l => ({ ...l, endpoints: true }));
        analyticsService.getEndpoints(from, to, {
            tool: toolFilter, search: endpointSearch, sort: endpointSort, page: endpointPage
        })
            .then(res => setEndpoints(res.data?.data || res.data))
            .catch(() => setEndpoints(null))
            .finally(() => setLoading(l => ({ ...l, endpoints: false })));
    }, [from, to, toolFilter, endpointSearch, endpointSort, endpointPage]);

    const fetchErrors = useCallback(() => {
        setLoading(l => ({ ...l, errors: true }));
        analyticsService.getErrors(from, to, {
            tool: toolFilter, status: errorStatus, search: errorSearch, page: errorPage
        })
            .then(res => setErrors(res.data?.data || res.data))
            .catch(() => setErrors(null))
            .finally(() => setLoading(l => ({ ...l, errors: false })));
    }, [from, to, toolFilter, errorStatus, errorSearch, errorPage]);

    const fetchLive = useCallback(() => {
        analyticsService.getLiveMonitor(5, 50)
            .then(res => setLive(res.data?.data || res.data))
            .catch(() => { });
    }, []);

    // ─── Effects ───
    useEffect(() => {
        if (activeTab === 'overview') fetchOverview();
    }, [activeTab, fetchOverview]);

    useEffect(() => {
        if (activeTab === 'users') fetchUsers();
    }, [activeTab, fetchUsers]);

    useEffect(() => {
        if (activeTab === 'endpoints') fetchEndpoints();
    }, [activeTab, fetchEndpoints]);

    useEffect(() => {
        if (activeTab === 'errors') fetchErrors();
    }, [activeTab, fetchErrors]);

    // Live monitor polling
    useEffect(() => {
        if (activeTab === 'live' && liveEnabled) {
            fetchLive();
            liveTimerRef.current = setInterval(fetchLive, 5000);
            return () => clearInterval(liveTimerRef.current);
        }
        return () => clearInterval(liveTimerRef.current);
    }, [activeTab, liveEnabled, fetchLive]);

    // ─── Tab toggle ───
    const toggleTab = (tab) => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    // ═══════════════════════════════════════════════
    // TAB 1: OVERVIEW
    // ═══════════════════════════════════════════════
    const renderOverview = () => {
        const kpi = overview?.kpi || {};
        const timeline = overview?.timeline;
        const topTools = overview?.topTools || [];
        const byServer = overview?.requestsByServer || {};

        const lineData = timeline ? {
            labels: timeline.labels,
            datasets: (timeline.datasets || []).map((ds, i) => ({
                label: ds.label,
                data: ds.data,
                borderColor: Object.values(COLORS)[i] || CHART_COLORS[i],
                backgroundColor: 'transparent',
                borderWidth: 2,
                pointRadius: 1,
                tension: 0.4,
            }))
        } : null;

        const doughnutData = Object.keys(byServer).length ? {
            labels: Object.keys(byServer),
            datasets: [{
                data: Object.values(byServer),
                backgroundColor: [COLORS.api, COLORS.api2, COLORS.api3],
                borderWidth: 0,
            }]
        } : null;

        const toolBarData = topTools.length ? {
            labels: topTools.map(t => t.tool),
            datasets: [{
                label: 'Requests',
                data: topTools.map(t => t.count),
                backgroundColor: CHART_COLORS.slice(0, topTools.length),
                borderRadius: 6,
                barPercentage: 0.6,
            }]
        } : null;

        return (
            <div>
                {/* KPI Cards */}
                <Row>
                    <Col md="3">
                        <KpiCard icon="fas fa-server" label="Total Requests" value={formatNumber(kpi.totalRequests)} subValue={`Today: ${formatNumber(kpi.requestsToday)}`} color={COLORS.primary} />
                    </Col>
                    <Col md="3">
                        <KpiCard icon="fas fa-exclamation-triangle" label="Error Rate" value={kpi.errorRate != null ? `${kpi.errorRate}%` : '-'} subValue={`${formatNumber(kpi.totalErrors)} errors`} color={COLORS.danger} />
                    </Col>
                    <Col md="3">
                        <KpiCard icon="fas fa-users" label="Active Users" value={formatNumber(kpi.uniqueUsers)} subValue={`${formatNumber(kpi.totalLogins)} logins`} color={COLORS.success} />
                    </Col>
                    <Col md="3">
                        <KpiCard icon="fas fa-clock" label="Avg Response" value={formatMs(kpi.avgResponseTime)} color={COLORS.info} />
                    </Col>
                </Row>

                {/* Timeline Chart */}
                {lineData && (
                    <Card style={{ borderRadius: '16px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', marginBottom: '16px' }}>
                        <CardHeader style={{ border: 'none', background: 'transparent', paddingBottom: 0 }}>
                            <CardTitle tag="h6" style={{ fontWeight: 600, color: COLORS.dark }}>
                                <i className="fas fa-chart-line mr-2" style={{ color: COLORS.primary }} />
                                Request Timeline
                            </CardTitle>
                        </CardHeader>
                        <CardBody>
                            <Line data={lineData} options={{
                                maintainAspectRatio: true,
                                legend: { position: 'top', labels: { fontSize: 12, usePointStyle: true } },
                                scales: {
                                    yAxes: [{ ticks: { beginAtZero: true, fontSize: 11 }, gridLines: { color: '#f0f0f0' } }],
                                    xAxes: [{ ticks: { fontSize: 11 }, gridLines: { display: false } }]
                                }
                            }} height={80} />
                        </CardBody>
                    </Card>
                )}

                {/* Bottom row: Doughnut + Bar */}
                <Row>
                    {doughnutData && (
                        <Col md="4">
                            <Card style={{ borderRadius: '16px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                                <CardHeader style={{ border: 'none', background: 'transparent', paddingBottom: 0 }}>
                                    <CardTitle tag="h6" style={{ fontWeight: 600, color: COLORS.dark }}>
                                        <i className="fas fa-pie-chart mr-2" style={{ color: COLORS.info }} />
                                        By Server
                                    </CardTitle>
                                </CardHeader>
                                <CardBody>
                                    <Doughnut data={doughnutData} options={{
                                        cutoutPercentage: 65,
                                        legend: { position: 'bottom', labels: { fontSize: 11, usePointStyle: true } },
                                    }} />
                                </CardBody>
                            </Card>
                        </Col>
                    )}
                    {toolBarData && (
                        <Col md="8">
                            <Card style={{ borderRadius: '16px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                                <CardHeader style={{ border: 'none', background: 'transparent', paddingBottom: 0 }}>
                                    <CardTitle tag="h6" style={{ fontWeight: 600, color: COLORS.dark }}>
                                        <i className="fas fa-chart-bar mr-2" style={{ color: COLORS.success }} />
                                        Top Tools
                                    </CardTitle>
                                </CardHeader>
                                <CardBody>
                                    <HorizontalBar data={toolBarData} options={{
                                        legend: { display: false },
                                        scales: {
                                            xAxes: [{ ticks: { beginAtZero: true, fontSize: 11 }, gridLines: { color: '#f0f0f0' } }],
                                            yAxes: [{ ticks: { fontSize: 11 }, gridLines: { display: false } }]
                                        }
                                    }} height={120} />
                                </CardBody>
                            </Card>
                        </Col>
                    )}
                </Row>
            </div>
        );
    };

    // ═══════════════════════════════════════════════
    // TAB 2: USERS
    // ═══════════════════════════════════════════════
    const renderUsers = () => {
        const byTool = users?.byTool || [];
        const byApp = users?.byApp || [];
        const recent = users?.recentLogins || {};

        const toolDoughnut = byTool.length ? {
            labels: byTool.map(t => t.tool),
            datasets: [{
                data: byTool.map(t => t.loginCount),
                backgroundColor: CHART_COLORS.slice(0, byTool.length),
                borderWidth: 0,
            }]
        } : null;

        const appBarData = byApp.length ? {
            labels: byApp.map(a => `${a.appName || 'N/A'} ${a.appVersion || ''}`),
            datasets: [{
                label: 'Logins',
                data: byApp.map(a => a.count),
                backgroundColor: CHART_COLORS.slice(0, byApp.length),
                borderRadius: 6,
                barPercentage: 0.6,
            }]
        } : null;

        return (
            <div>
                {/* Search & Filter Bar */}
                <Card style={{ borderRadius: '16px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', marginBottom: '16px' }}>
                    <CardBody style={{ padding: '16px 20px' }}>
                        <Row>
                            <Col md="4">
                                <InputGroup size="sm">
                                    <InputGroupAddon addonType="prepend">
                                        <InputGroupText><i className="fas fa-search" /></InputGroupText>
                                    </InputGroupAddon>
                                    <Input placeholder="Search username..." value={userSearch}
                                        onChange={e => { setUserSearch(e.target.value); setUserPage(1); }}
                                        onKeyDown={e => e.key === 'Enter' && fetchUsers()}
                                    />
                                </InputGroup>
                            </Col>
                            <Col md="3">
                                <Input type="select" bsSize="sm" value={userApp}
                                    onChange={e => { setUserApp(e.target.value); setUserPage(1); }}>
                                    <option value="">All Apps</option>
                                    <option value="DHB Stream Pro">DHB Stream Pro</option>
                                    <option value="DHB Render">DHB Render</option>
                                    <option value="DHB Reup YouTube">DHB Reup YouTube</option>
                                    <option value="DHB Reup TikTok">DHB Reup TikTok</option>
                                    <option value="DHB Reup Facebook">DHB Reup Facebook</option>
                                    <option value="Web Browser">Web Browser</option>
                                </Input>
                            </Col>
                            <Col md="2">
                                <Button size="sm" color="primary" outline onClick={fetchUsers}>
                                    <i className="fas fa-filter mr-1" /> Filter
                                </Button>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {/* Charts */}
                <Row>
                    {toolDoughnut && (
                        <Col md="4">
                            <Card style={{ borderRadius: '16px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', marginBottom: '16px' }}>
                                <CardHeader style={{ border: 'none', background: 'transparent', paddingBottom: 0 }}>
                                    <CardTitle tag="h6" style={{ fontWeight: 600, color: COLORS.dark }}>Login by Tool</CardTitle>
                                </CardHeader>
                                <CardBody>
                                    <Doughnut data={toolDoughnut} options={{
                                        cutoutPercentage: 65,
                                        legend: { position: 'bottom', labels: { fontSize: 10, usePointStyle: true } },
                                    }} />
                                </CardBody>
                            </Card>
                        </Col>
                    )}
                    {appBarData && (
                        <Col md="8">
                            <Card style={{ borderRadius: '16px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', marginBottom: '16px' }}>
                                <CardHeader style={{ border: 'none', background: 'transparent', paddingBottom: 0 }}>
                                    <CardTitle tag="h6" style={{ fontWeight: 600, color: COLORS.dark }}>Login by App / Version</CardTitle>
                                </CardHeader>
                                <CardBody>
                                    <HorizontalBar data={appBarData} options={{
                                        legend: { display: false },
                                        scales: {
                                            xAxes: [{ ticks: { beginAtZero: true, fontSize: 11 }, gridLines: { color: '#f0f0f0' } }],
                                            yAxes: [{ ticks: { fontSize: 10 }, gridLines: { display: false } }]
                                        }
                                    }} height={Math.max(60, byApp.length * 20)} />
                                </CardBody>
                            </Card>
                        </Col>
                    )}
                </Row>

                {/* Recent Logins Table */}
                <Card style={{ borderRadius: '16px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                    <CardHeader style={{ border: 'none', background: 'transparent', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <CardTitle tag="h6" style={{ fontWeight: 600, color: COLORS.dark, margin: 0 }}>
                            <i className="fas fa-sign-in-alt mr-2" style={{ color: COLORS.primary }} />
                            Recent Logins
                        </CardTitle>
                        <span style={{ fontSize: '12px', color: COLORS.muted }}>
                            Total: {formatNumber(recent.total)} | Page {recent.page || 1}/{recent.totalPages || 1}
                        </span>
                    </CardHeader>
                    <CardBody style={{ padding: '0 20px 20px' }}>
                        <Table responsive hover size="sm" style={{ fontSize: '13px' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #f0f0f0' }}>
                                    <th>Username</th>
                                    <th>Tool</th>
                                    <th>App / Version</th>
                                    <th>IP</th>
                                    <th>Server</th>
                                    <th>Time</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(recent.data || []).map((row, i) => (
                                    <tr key={i}>
                                        <td style={{ fontWeight: 600 }}>{row.username || '-'}</td>
                                        <td><Badge color="light" style={{ color: COLORS.dark }}>{row.tool}</Badge></td>
                                        <td>{row.appName ? `${row.appName} ${row.appVersion || ''}` : '-'}</td>
                                        <td style={{ fontSize: '12px', color: COLORS.muted }}>{row.ip}</td>
                                        <td><Badge color={row.serverName === 'api' ? 'primary' : row.serverName === 'api2' ? 'success' : 'warning'} pill style={{ fontSize: '10px' }}>{row.serverName}</Badge></td>
                                        <td style={{ fontSize: '12px', color: COLORS.muted }}>{timeAgo(row.createdAt)}</td>
                                    </tr>
                                ))}
                                {(!recent.data || recent.data.length === 0) && (
                                    <tr><td colSpan="6" className="text-center" style={{ color: COLORS.muted, padding: '30px' }}>No login data available</td></tr>
                                )}
                            </tbody>
                        </Table>
                        <PaginationBar page={userPage} totalPages={recent.totalPages} onPageChange={setUserPage} />
                    </CardBody>
                </Card>
            </div>
        );
    };

    // ═══════════════════════════════════════════════
    // TAB 3: ENDPOINTS
    // ═══════════════════════════════════════════════
    const renderEndpoints = () => {
        const eps = endpoints?.endpoints || [];
        const total = endpoints?.total || 0;
        const totalPages = endpoints?.totalPages || 1;

        return (
            <div>
                {/* Search & Sort */}
                <Card style={{ borderRadius: '16px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', marginBottom: '16px' }}>
                    <CardBody style={{ padding: '16px 20px' }}>
                        <Row>
                            <Col md="5">
                                <InputGroup size="sm">
                                    <InputGroupAddon addonType="prepend">
                                        <InputGroupText><i className="fas fa-search" /></InputGroupText>
                                    </InputGroupAddon>
                                    <Input placeholder="Search endpoint path... (e.g. login, refresh)" value={endpointSearch}
                                        onChange={e => { setEndpointSearch(e.target.value); setEndpointPage(1); }}
                                        onKeyDown={e => e.key === 'Enter' && fetchEndpoints()}
                                    />
                                </InputGroup>
                            </Col>
                            <Col md="3">
                                <Input type="select" bsSize="sm" value={endpointSort}
                                    onChange={e => { setEndpointSort(e.target.value); setEndpointPage(1); }}>
                                    <option value="calls">Sort: Most Calls</option>
                                    <option value="errors">Sort: Most Errors</option>
                                    <option value="time">Sort: Slowest</option>
                                </Input>
                            </Col>
                            <Col md="2">
                                <Button size="sm" color="primary" outline onClick={fetchEndpoints}>
                                    <i className="fas fa-filter mr-1" /> Filter
                                </Button>
                            </Col>
                            <Col md="2" className="text-right">
                                <span style={{ fontSize: '12px', color: COLORS.muted, lineHeight: '31px' }}>{formatNumber(total)} endpoints</span>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {/* Endpoints Table */}
                <Card style={{ borderRadius: '16px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                    <CardBody style={{ padding: '0 20px 20px' }}>
                        <Table responsive hover size="sm" style={{ fontSize: '13px' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #f0f0f0' }}>
                                    <th style={{ width: '30px' }}></th>
                                    <th>Method</th>
                                    <th>Path</th>
                                    <th className="text-right">Calls</th>
                                    <th className="text-right">Errors</th>
                                    <th className="text-right">Avg Time</th>
                                </tr>
                            </thead>
                            <tbody>
                                {eps.map((ep, i) => (
                                    <React.Fragment key={i}>
                                        <tr style={{ cursor: 'pointer' }} onClick={() => setExpandedEndpoints(prev => ({ ...prev, [i]: !prev[i] }))}>
                                            <td>
                                                <i className={`fas fa-chevron-${expandedEndpoints[i] ? 'down' : 'right'}`} style={{ fontSize: '10px', color: COLORS.muted }} />
                                            </td>
                                            <td><Badge color={ep.method === 'GET' ? 'success' : ep.method === 'POST' ? 'primary' : ep.method === 'DELETE' ? 'danger' : 'warning'} style={{ fontSize: '10px' }}>{ep.method}</Badge></td>
                                            <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{ep.normalizedPath}</td>
                                            <td className="text-right" style={{ fontWeight: 600 }}>{formatNumber(ep.totalCalls)}</td>
                                            <td className="text-right">{ep.errorCount > 0 ? <span style={{ color: COLORS.danger, fontWeight: 600 }}>{formatNumber(ep.errorCount)}</span> : <span style={{ color: COLORS.muted }}>0</span>}</td>
                                            <td className="text-right">{formatMs(ep.avgResponseTime)}</td>
                                        </tr>
                                        <tr>
                                            <td colSpan="6" style={{ padding: 0, border: 'none' }}>
                                                <Collapse isOpen={expandedEndpoints[i]}>
                                                    <div style={{ padding: '12px 20px', background: '#f8f9fe', borderRadius: '8px', margin: '0 0 8px 30px', fontSize: '12px' }}>
                                                        <Row>
                                                            <Col md="3">
                                                                <strong style={{ color: COLORS.dark }}>Response Time</strong>
                                                                <div>Avg: {formatMs(ep.avgResponseTime)}</div>
                                                                <div>Max: {formatMs(ep.maxResponseTime)}</div>
                                                                <div>Min: {formatMs(ep.minResponseTime)}</div>
                                                            </Col>
                                                            <Col md="3">
                                                                <strong style={{ color: COLORS.dark }}>By Server</strong>
                                                                {ep.byServer && Object.entries(ep.byServer).map(([srv, cnt]) => (
                                                                    <div key={srv}>{srv}: {formatNumber(cnt)}</div>
                                                                ))}
                                                            </Col>
                                                            <Col md="3">
                                                                <strong style={{ color: COLORS.dark }}>By Tool</strong>
                                                                {(ep.byTool || []).slice(0, 5).map((t, j) => (
                                                                    <div key={j}>{t.tool}: {formatNumber(t.count)}</div>
                                                                ))}
                                                            </Col>
                                                            <Col md="3">
                                                                <strong style={{ color: COLORS.dark }}>Status Codes</strong>
                                                                {ep.byStatusCode && Object.entries(ep.byStatusCode).map(([code, cnt]) => (
                                                                    <div key={code}>{statusBadge(Number(code))} {formatNumber(cnt)}</div>
                                                                ))}
                                                            </Col>
                                                        </Row>
                                                        <div style={{ marginTop: '8px', color: COLORS.muted }}>
                                                            Unique users: {formatNumber(ep.uniqueUsers)} | Error rate: {ep.errorRate != null ? `${ep.errorRate}%` : '-'}
                                                        </div>
                                                    </div>
                                                </Collapse>
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                ))}
                                {eps.length === 0 && (
                                    <tr><td colSpan="6" className="text-center" style={{ color: COLORS.muted, padding: '30px' }}>No endpoint data available</td></tr>
                                )}
                            </tbody>
                        </Table>
                        <PaginationBar page={endpointPage} totalPages={totalPages} onPageChange={setEndpointPage} />
                    </CardBody>
                </Card>
            </div>
        );
    };

    // ═══════════════════════════════════════════════
    // TAB 4: ERRORS
    // ═══════════════════════════════════════════════
    const renderErrors = () => {
        const summary = errors?.summary || {};
        const byHour = errors?.errorsByHour;
        const errList = errors?.errors || {};

        const barData = byHour ? {
            labels: byHour.labels,
            datasets: [{
                label: 'Errors',
                data: byHour.values,
                backgroundColor: COLORS.danger + '88',
                borderColor: COLORS.danger,
                borderWidth: 1,
                borderRadius: 4,
                barPercentage: 0.7,
            }]
        } : null;

        return (
            <div>
                {/* Summary Cards */}
                <Row>
                    <Col md="3">
                        <KpiCard icon="fas fa-exclamation-circle" label="4xx Errors" value={formatNumber(summary.total4xx)} color={COLORS.warning} />
                    </Col>
                    <Col md="3">
                        <KpiCard icon="fas fa-times-circle" label="5xx Errors" value={formatNumber(summary.total5xx)} color={COLORS.danger} />
                    </Col>
                    <Col md="3">
                        <KpiCard icon="fas fa-crosshairs" label="Top Error Path" value={summary.topErrorPath || '-'} color={COLORS.dark} />
                    </Col>
                    <Col md="3">
                        <KpiCard icon="fas fa-hashtag" label="Top Error Code" value={summary.topErrorCode || '-'} color={COLORS.info} />
                    </Col>
                </Row>

                {/* Errors by Hour Chart */}
                {barData && (
                    <Card style={{ borderRadius: '16px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', marginBottom: '16px' }}>
                        <CardHeader style={{ border: 'none', background: 'transparent', paddingBottom: 0 }}>
                            <CardTitle tag="h6" style={{ fontWeight: 600, color: COLORS.dark }}>
                                <i className="fas fa-chart-bar mr-2" style={{ color: COLORS.danger }} />
                                Errors by Hour
                            </CardTitle>
                        </CardHeader>
                        <CardBody>
                            <Bar data={barData} options={{
                                legend: { display: false },
                                scales: {
                                    yAxes: [{ ticks: { beginAtZero: true, fontSize: 11 }, gridLines: { color: '#f0f0f0' } }],
                                    xAxes: [{ ticks: { fontSize: 11 }, gridLines: { display: false } }]
                                }
                            }} height={60} />
                        </CardBody>
                    </Card>
                )}

                {/* Search & Filter */}
                <Card style={{ borderRadius: '16px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', marginBottom: '16px' }}>
                    <CardBody style={{ padding: '16px 20px' }}>
                        <Row>
                            <Col md="5">
                                <InputGroup size="sm">
                                    <InputGroupAddon addonType="prepend">
                                        <InputGroupText><i className="fas fa-search" /></InputGroupText>
                                    </InputGroupAddon>
                                    <Input placeholder="Search path, username, error..." value={errorSearch}
                                        onChange={e => { setErrorSearch(e.target.value); setErrorPage(1); }}
                                        onKeyDown={e => e.key === 'Enter' && fetchErrors()}
                                    />
                                </InputGroup>
                            </Col>
                            <Col md="3">
                                <Input type="select" bsSize="sm" value={errorStatus}
                                    onChange={e => { setErrorStatus(e.target.value); setErrorPage(1); }}>
                                    <option value="">All Status</option>
                                    <option value="4xx">4xx Only</option>
                                    <option value="5xx">5xx Only</option>
                                    <option value="401">401 Unauthorized</option>
                                    <option value="500">500 Internal</option>
                                </Input>
                            </Col>
                            <Col md="2">
                                <Button size="sm" color="primary" outline onClick={fetchErrors}>
                                    <i className="fas fa-filter mr-1" /> Filter
                                </Button>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {/* Error Table */}
                <Card style={{ borderRadius: '16px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                    <CardBody style={{ padding: '0 20px 20px' }}>
                        <Table responsive hover size="sm" style={{ fontSize: '13px' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #f0f0f0' }}>
                                    <th style={{ width: '30px' }}></th>
                                    <th>Status</th>
                                    <th>Path</th>
                                    <th>User</th>
                                    <th>Error</th>
                                    <th>Server</th>
                                    <th>Time</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(errList.data || []).map((err, i) => (
                                    <React.Fragment key={i}>
                                        <tr style={{ cursor: 'pointer' }} onClick={() => setExpandedErrors(prev => ({ ...prev, [i]: !prev[i] }))}>
                                            <td><i className={`fas fa-chevron-${expandedErrors[i] ? 'down' : 'right'}`} style={{ fontSize: '10px', color: COLORS.muted }} /></td>
                                            <td>{statusBadge(err.statusCode)}</td>
                                            <td style={{ fontFamily: 'monospace', fontSize: '12px', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{err.path}</td>
                                            <td style={{ fontWeight: 600 }}>{err.username || '-'}</td>
                                            <td style={{ fontSize: '12px', color: COLORS.muted, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{err.errorMessage || '-'}</td>
                                            <td><Badge color={err.serverName === 'api' ? 'primary' : err.serverName === 'api2' ? 'success' : 'warning'} pill style={{ fontSize: '10px' }}>{err.serverName}</Badge></td>
                                            <td style={{ fontSize: '12px', color: COLORS.muted }}>{timeAgo(err.createdAt)}</td>
                                        </tr>
                                        <tr>
                                            <td colSpan="7" style={{ padding: 0, border: 'none' }}>
                                                <Collapse isOpen={expandedErrors[i]}>
                                                    <div style={{ padding: '12px 20px', background: '#fff5f5', borderRadius: '8px', margin: '0 0 8px 30px', fontSize: '12px' }}>
                                                        <Row>
                                                            <Col md="6">
                                                                <div><strong>Full Path:</strong> <code style={{ fontSize: '11px' }}>{err.path}</code></div>
                                                                <div><strong>Normalized:</strong> <code style={{ fontSize: '11px' }}>{err.normalizedPath}</code></div>
                                                                <div><strong>Method:</strong> {err.method}</div>
                                                                <div><strong>Response Time:</strong> {formatMs(err.responseTime)}</div>
                                                            </Col>
                                                            <Col md="6">
                                                                <div><strong>IP:</strong> {err.ip}</div>
                                                                <div><strong>User-Agent:</strong> <span style={{ fontSize: '11px', wordBreak: 'break-all' }}>{err.userAgent || '-'}</span></div>
                                                                <div><strong>App:</strong> {err.appName || '-'} {err.appVersion || ''}</div>
                                                                <div><strong>Timestamp:</strong> {err.createdAt ? new Date(err.createdAt).toLocaleString() : '-'}</div>
                                                            </Col>
                                                        </Row>
                                                        {err.errorMessage && (
                                                            <div style={{ marginTop: '8px', padding: '8px', background: '#fee', borderRadius: '4px', fontFamily: 'monospace', fontSize: '11px', wordBreak: 'break-all' }}>
                                                                {err.errorMessage}
                                                            </div>
                                                        )}
                                                    </div>
                                                </Collapse>
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                ))}
                                {(!errList.data || errList.data.length === 0) && (
                                    <tr><td colSpan="7" className="text-center" style={{ color: COLORS.muted, padding: '30px' }}>No errors found 🎉</td></tr>
                                )}
                            </tbody>
                        </Table>
                        <PaginationBar page={errorPage} totalPages={errList.totalPages} onPageChange={setErrorPage} />
                    </CardBody>
                </Card>
            </div>
        );
    };

    // ═══════════════════════════════════════════════
    // TAB 5: LIVE MONITOR
    // ═══════════════════════════════════════════════
    const renderLive = () => {
        const reqs = live?.recentRequests || [];
        const rpm = live?.requestsPerMinute || 0;
        const servers = live?.activeServers || [];

        return (
            <div>
                {/* Live Header */}
                <Card style={{ borderRadius: '16px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', marginBottom: '16px' }}>
                    <CardBody style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{
                                    width: '12px', height: '12px', borderRadius: '50%',
                                    backgroundColor: liveEnabled ? '#2dce89' : '#adb5bd',
                                    display: 'inline-block',
                                    animation: liveEnabled ? 'pulse 2s infinite' : 'none',
                                    boxShadow: liveEnabled ? '0 0 8px #2dce89' : 'none',
                                }} />
                                <span style={{ fontWeight: 700, color: COLORS.dark }}>
                                    {liveEnabled ? 'Live' : 'Paused'}
                                </span>
                            </div>
                            <span style={{ fontSize: '14px', color: COLORS.muted }}>
                                <i className="fas fa-tachometer-alt mr-1" />
                                {rpm} req/min
                            </span>
                            <span style={{ fontSize: '14px', color: COLORS.muted }}>
                                Servers: {servers.map(s => (
                                    <Badge key={s} color="success" pill style={{ fontSize: '10px', marginLeft: '4px' }}>{s} ✓</Badge>
                                ))}
                            </span>
                        </div>
                        <Button size="sm" color={liveEnabled ? 'warning' : 'success'} outline
                            onClick={() => setLiveEnabled(!liveEnabled)}>
                            <i className={`fas fa-${liveEnabled ? 'pause' : 'play'} mr-1`} />
                            {liveEnabled ? 'Pause' : 'Resume'}
                        </Button>
                    </CardBody>
                </Card>

                {/* Live Request Table */}
                <Card style={{ borderRadius: '16px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                    <CardBody style={{ padding: '0 20px 20px' }}>
                        <Table responsive hover size="sm" style={{ fontSize: '13px' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #f0f0f0' }}>
                                    <th>Method</th>
                                    <th>Path</th>
                                    <th>User</th>
                                    <th>Status</th>
                                    <th>Time</th>
                                    <th>Server</th>
                                    <th>When</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reqs.map((req, i) => (
                                    <tr key={i} style={{
                                        backgroundColor: req.statusCode >= 500 ? '#fff5f5' : req.statusCode >= 400 ? '#fffbf0' : 'transparent',
                                    }}>
                                        <td><Badge color={req.method === 'GET' ? 'success' : req.method === 'POST' ? 'primary' : req.method === 'DELETE' ? 'danger' : 'warning'} style={{ fontSize: '10px' }}>{req.method}</Badge></td>
                                        <td style={{ fontFamily: 'monospace', fontSize: '12px', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{req.normalizedPath || req.path}</td>
                                        <td style={{ fontWeight: req.username ? 600 : 400, color: req.username ? COLORS.dark : COLORS.muted }}>{req.username || '-'}</td>
                                        <td>{statusBadge(req.statusCode)}</td>
                                        <td>{formatMs(req.responseTime)}</td>
                                        <td><Badge color={req.serverName === 'api' ? 'primary' : req.serverName === 'api2' ? 'success' : 'warning'} pill style={{ fontSize: '10px' }}>{req.serverName}</Badge></td>
                                        <td style={{ fontSize: '12px', color: COLORS.muted }}>{timeAgo(req.createdAt)}</td>
                                    </tr>
                                ))}
                                {reqs.length === 0 && (
                                    <tr><td colSpan="7" className="text-center" style={{ color: COLORS.muted, padding: '30px' }}>
                                        {liveEnabled ? <><Spinner size="sm" /> Waiting for requests...</> : 'Paused'}
                                    </td></tr>
                                )}
                            </tbody>
                        </Table>
                    </CardBody>
                </Card>

                {/* Pulse animation */}
                <style>{`
                    @keyframes pulse {
                        0% { opacity: 1; }
                        50% { opacity: 0.4; }
                        100% { opacity: 1; }
                    }
                `}</style>
            </div>
        );
    };

    // ═══════════════════════════════════════════════
    // MAIN RENDER
    // ═══════════════════════════════════════════════
    const tabStyle = (tab) => ({
        cursor: 'pointer',
        fontWeight: activeTab === tab ? 700 : 400,
        color: activeTab === tab ? COLORS.primary : COLORS.muted,
        borderBottom: activeTab === tab ? `3px solid ${COLORS.primary}` : '3px solid transparent',
        padding: '10px 20px',
        fontSize: '14px',
        transition: 'all 0.2s ease',
        background: 'transparent',
        border: 'none',
        borderRadius: 0,
    });

    return (
        <div className="content">
            {/* Global Filter Bar */}
            <Card style={{ borderRadius: '16px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', marginBottom: '20px' }}>
                <CardBody style={{ padding: '12px 20px' }}>
                    <Row className="align-items-center">
                        <Col md="auto">
                            <Label style={{ fontSize: '12px', fontWeight: 600, color: COLORS.muted, marginBottom: 0, marginRight: '8px' }}>
                                <i className="fas fa-calendar-alt mr-1" />
                                Period
                            </Label>
                            <ButtonGroup size="sm">
                                {DATE_RANGES.map(r => (
                                    <Button key={r.value} color={dateRange === r.value ? 'primary' : 'secondary'}
                                        outline={dateRange !== r.value}
                                        onClick={() => setDateRange(r.value)}
                                        style={{ fontSize: '12px' }}>
                                        {r.label}
                                    </Button>
                                ))}
                            </ButtonGroup>
                        </Col>
                        <Col md="3">
                            <Input type="select" bsSize="sm" value={toolFilter}
                                onChange={e => setToolFilter(e.target.value)}
                                style={{ fontSize: '12px' }}>
                                {TOOLS.map(t => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </Input>
                        </Col>
                        <Col md="auto" className="ml-auto">
                            {loading[activeTab] && <Spinner size="sm" color="primary" />}
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {/* Tab Navigation */}
            <div style={{ display: 'flex', borderBottom: '1px solid #e9ecef', marginBottom: '20px', overflowX: 'auto' }}>
                <button style={tabStyle('overview')} onClick={() => toggleTab('overview')}>
                    <i className="fas fa-tachometer-alt mr-2" />Overview
                </button>
                <button style={tabStyle('users')} onClick={() => toggleTab('users')}>
                    <i className="fas fa-users mr-2" />Users
                </button>
                <button style={tabStyle('endpoints')} onClick={() => toggleTab('endpoints')}>
                    <i className="fas fa-code mr-2" />Endpoints
                </button>
                <button style={tabStyle('errors')} onClick={() => toggleTab('errors')}>
                    <i className="fas fa-bug mr-2" />Errors
                </button>
                <button style={tabStyle('live')} onClick={() => toggleTab('live')}>
                    <i className="fas fa-broadcast-tower mr-2" />Live Monitor
                </button>
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'users' && renderUsers()}
            {activeTab === 'endpoints' && renderEndpoints()}
            {activeTab === 'errors' && renderErrors()}
            {activeTab === 'live' && renderLive()}
        </div>
    );
};

export default AnalyzePage;
